import React, { FC, useMemo } from 'react';

import Button from '../../components/UI/button';
import { useCapStatus } from '../../hooks/useCapStatus';
import { EPaymentStatus } from '../../api';
import { translate as _t } from '../../utils/translation';

import './status.scss';

interface Props {
  sessionId?: string;
  checkoutId?: string;
  env?: string;
}

const CapStatus: FC<Props> = ({ sessionId: propSessionId, checkoutId: propCheckoutId, env: propEnv }) => {
  const {
    paymentData,
    isLoading,
    error,
  } = useCapStatus(propSessionId, propCheckoutId, propEnv);

  // Determine status display info
  const statusInfo = useMemo(() => {
    if (!paymentData) return null;

    const statusMap: Record<EPaymentStatus, { icon: string; title: string; className: string }> = {
      [EPaymentStatus.Completed]: {
        icon: '✓',
        title: _t('Payment Successful', 'CAP.PAYMENT_SUCCESSFUL'),
        className: 'success',
      },
      [EPaymentStatus.Pending]: {
        icon: '⏳',
        title: _t('Payment Pending', 'CAP.PAYMENT_PENDING'),
        className: 'pending',
      },
      [EPaymentStatus.Processing]: {
        icon: '⚙',
        title: _t('Payment Processing', 'CAP.PAYMENT_PROCESSING'),
        className: 'processing',
      },
      [EPaymentStatus.Failed]: {
        icon: '✕',
        title: _t('Payment Failed', 'CAP.PAYMENT_FAILED'),
        className: 'failed',
      },
    };

    return statusMap[paymentData.status as EPaymentStatus] || statusMap[EPaymentStatus.Pending];
  }, [paymentData]);

  // Loading state
  if (isLoading) {
    return (
      <div className="cap-status">
        <div className="cap-status-container">
          <p className="cap-status__loading">{_t('Loading payment status...', 'CAP.LOADING_STATUS')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !paymentData) {
    return (
      <div className="cap-status">
        <div className="cap-status-container">
          <div className="status-icon failed">✕</div>
          <h2 className="status-title">{_t('Error', 'CAP.ERROR')}</h2>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cap-status">
      <div className="cap-status-container">
        {/* Status Icon */}
        <div className={`status-icon ${statusInfo?.className || ''}`}>
          {statusInfo?.icon}
        </div>

        {/* Status Title */}
        <h1 className="status-title">{statusInfo?.title}</h1>

        {/* Payment Details */}
        {paymentData && (
          <div className="payment-details">
            <div className="payment-details-row">
              <span className="label">{_t('Amount', 'CAP.AMOUNT')}</span>
              <span className="value">{paymentData.amount} {paymentData.currency}</span>
            </div>

            {paymentData.payment_brand && (
              <div className="payment-details-row">
                <span className="label">{_t('Payment Method', 'CAP.PAYMENT_METHOD')}</span>
                <span className="value">{paymentData.payment_brand}</span>
              </div>
            )}

            <div className="payment-details-row">
              <span className="label">{_t('Transaction ID', 'CAP.TRANSACTION_ID')}</span>
              <span className="value">{paymentData.payment_id}</span>
            </div>

            <div className="payment-details-row">
              <span className="label">{_t('Date', 'CAP.DATE')}</span>
              <span className="value">
                {new Date(paymentData.timestamp).toLocaleString()}
              </span>
            </div>

            {paymentData.result?.message && (
              <div className="payment-details-row message">
                <span className="label">{_t('Message', 'CAP.MESSAGE')}</span>
                <span className="value">{paymentData.result.message}</span>
              </div>
            )}
          </div>
        )}

        {/* New Account Created Notice */}
        {paymentData?.new_account_created && (
          <div className="new-account-notice">
            {_t('Your account has been created. A temporary password has been sent to your email address.', 'CAP.NEW_ACCOUNT_CREATED')}
          </div>
        )}
      </div>
    </div>
  );
};

export default CapStatus;
